import React, { useState, useEffect, useMemo } from 'react';

const IntroAnimation = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [logs, setLogs] = useState([]);

  // Secuencia de arranque OCBET (español MX)
  const steps = useMemo(() => [
    { message: 'Inicializando sistema OCBET...', duration: 700 },
    { message: 'Conectando a la red de datos...', duration: 600 },
    { message: 'Calibrando sensores de patrones...', duration: 650 },
    { message: 'Sincronizando servidores...', duration: 600 },
    { message: 'Preparando la plataforma...', duration: 550 }
  ], []);

  const getCurrentMessage = () => {
    if (currentStep >= 0 && currentStep < steps.length && steps[currentStep]?.message) {
      return steps[currentStep].message;
    }
    return 'Procesando...';
  };

  useEffect(() => {
    let stepIndex = 0;
    let progressValue = 0;
    let isMounted = true;

    const progressInterval = setInterval(() => {
      if (!isMounted) return;
      progressValue = Math.min(progressValue + 1.2, 100);
      setProgress(progressValue);
    }, 35);

    const runStep = () => {
      if (!isMounted) return;

      if (stepIndex >= 0 && stepIndex < steps.length) {
        const currentStepData = steps[stepIndex];

        if (currentStepData) {
          setCurrentStep(stepIndex);

          const timestamp = new Date().toLocaleTimeString('es-MX', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZone: 'America/Mexico_City'
          });

          const message = currentStepData.message ?? 'Procesando...';

          setLogs(prev => [...prev, {
            time: timestamp,
            message: message.replace('...', ''),
            status: 'ok'
          }]);

          const duration = currentStepData.duration ?? 600;
          stepIndex++;

          if (stepIndex < steps.length) {
            setTimeout(runStep, duration);
          }
        }
      }
    };

    const startTimeout = setTimeout(runStep, 300);

    const completeTimeout = setTimeout(() => {
      if (isMounted && onComplete) {
        onComplete();
      }
    }, 3400);

    return () => {
      isMounted = false;
      clearInterval(progressInterval);
      clearTimeout(startTimeout);
      clearTimeout(completeTimeout);
    };
  }, [onComplete, steps]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden" data-testid="ocbet-intro">

      {/* Fondo oscuro cálido */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 30%, #3a1a05 0%, #1f0f04 35%, #120902 70%, #0a0603 100%)',
        }}
      />

      {/* Nubes de energía */}
      <div
        className="absolute top-[-200px] right-[-150px] w-[700px] h-[700px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(249, 115, 22, 0.22) 0%, transparent 60%)',
          filter: 'blur(80px)',
          animation: 'nebula-pulse 6s ease-in-out infinite',
        }}
      />
      <div
        className="absolute bottom-[-200px] left-[-150px] w-[600px] h-[600px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(245, 158, 11, 0.16) 0%, transparent 60%)',
          filter: 'blur(80px)',
          animation: 'nebula-pulse 7s ease-in-out infinite 1.5s',
        }}
      />

      {/* Chispas / estrellas */}
      {[...Array(60)].map((_, i) => (
        <div
          key={`star-${i}`}
          className="absolute rounded-full"
          style={{
            width: `${1 + Math.random() * 2}px`,
            height: `${1 + Math.random() * 2}px`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            background: i % 4 === 0 ? '#fdba74' : i % 4 === 1 ? '#fbbf24' : i % 4 === 2 ? '#fb923c' : '#ffffff',
            boxShadow: '0 0 4px currentColor',
            opacity: 0.4 + Math.random() * 0.5,
            animation: `intro-twinkle ${2 + Math.random() * 3}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 4}s`,
          }}
        />
      ))}

      {/* Estrella fugaz */}
      <div
        className="absolute"
        style={{
          top: '15%',
          left: '-10%',
          width: '120px',
          height: '1.5px',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.8), rgba(249,115,22,0.6), transparent)',
          animation: 'shoot 5s linear infinite',
        }}
      />

      {/* Contenido principal */}
      <div className="relative z-10 w-full max-w-md px-6">

        {/* Emblema OCBET */}
        <div className="flex justify-center mb-6">
          <div className="relative w-28 h-28 sm:w-32 sm:h-32" data-testid="ocbet-emblem-intro">
            <div
              className="absolute inset-[-30px] rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(249, 115, 22, 0.5) 0%, rgba(245, 158, 11, 0.28) 35%, transparent 70%)',
                filter: 'blur(20px)',
                animation: 'aura-pulse 2.5s ease-in-out infinite',
              }}
            />
            <img
              src="/assets/ocbet/ocbet-emblem.png"
              alt="OCBET"
              draggable={false}
              className="relative w-full h-full object-contain"
              style={{
                filter: 'drop-shadow(0 0 18px rgba(249, 115, 22, 0.85)) drop-shadow(0 0 32px rgba(245, 158, 11, 0.55))',
                animation: 'astronaut-float 3.5s ease-in-out infinite',
              }}
            />
            <div
              className="absolute inset-[-20px] rounded-full pointer-events-none"
              style={{
                border: '1px dashed rgba(249, 115, 22, 0.35)',
                animation: 'orbit-spin 12s linear infinite',
              }}
            />
          </div>
        </div>

        {/* Título */}
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-black tracking-[0.2em] mb-1" style={{ letterSpacing: '0.25em' }}>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-300 via-amber-300 to-orange-400">OCBET</span>
          </h1>
          <p className="text-[10px] text-gray-500 font-mono uppercase tracking-[0.3em]">
            Sistema de señales · v2.4.1
          </p>
        </div>

        {/* Progreso */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-orange-300/80 font-mono">
              {getCurrentMessage()}
            </span>
            <span className="text-xs text-gray-500 font-mono">
              {Math.round(progress)}%
            </span>
          </div>

          <div className="relative h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(249, 115, 22, 0.08)' }}>
            <div
              className="h-full rounded-full relative transition-all duration-100 ease-out"
              style={{
                width: `${progress}%`,
                background: 'linear-gradient(90deg, #f97316 0%, #fbbf24 50%, #f97316 100%)',
                boxShadow: '0 0 12px rgba(249, 115, 22, 0.7), 0 0 24px rgba(245, 158, 11, 0.4)',
              }}
            >
              <div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                  animation: 'shine 2s ease-in-out infinite',
                }}
              />
            </div>
          </div>
        </div>

        {/* Salida de log */}
        <div className="rounded-lg overflow-hidden font-mono backdrop-blur-sm" style={{
          background: 'rgba(31, 15, 4, 0.7)',
          border: '1px solid rgba(249, 115, 22, 0.18)',
        }}>
          <div className="px-3 py-2 flex items-center gap-2" style={{
            borderBottom: '1px solid rgba(249, 115, 22, 0.12)',
            background: 'rgba(249, 115, 22, 0.04)',
          }}>
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[9px] text-orange-300/70 uppercase tracking-[0.2em]">Centro de control</span>
          </div>

          <div className="p-3 space-y-1.5 min-h-[120px]">
            {logs.map((log, index) => (
              <div
                key={index}
                className="flex items-center gap-3 text-[10px] animate-fadeIn"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <span className="text-orange-700 w-16 shrink-0">{log.time}</span>
                <span className="text-gray-300">{log.message}</span>
                <span className="text-emerald-400 ml-auto">[{log.status}]</span>
              </div>
            ))}

            {progress < 100 && (
              <div className="flex items-center gap-1 text-[10px] text-orange-400">
                <span className="animate-blink">▊</span>
              </div>
            )}
          </div>
        </div>

        {/* Estado */}
        <div className="mt-4 flex items-center justify-center gap-2">
          <div className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${
            progress >= 100 ? 'bg-emerald-400' : 'bg-orange-400 animate-pulse'
          }`} />
          <span className="text-[9px] text-gray-500 font-mono uppercase tracking-[0.25em]">
            {progress >= 100 ? 'Listo para comenzar' : 'Preparando todo'}
          </span>
        </div>
      </div>

      {/* Animaciones */}
      <style>{`
        @keyframes shine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.2; }
        }
        .animate-blink { animation: blink 1s step-end infinite; }
        @keyframes nebula-pulse {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }
        @keyframes intro-twinkle {
          0%, 100% { opacity: 0.2; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes astronaut-float {
          0%, 100% { transform: translateY(0) rotate(-2deg); }
          50% { transform: translateY(-8px) rotate(2deg); }
        }
        @keyframes aura-pulse {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.08); }
        }
        @keyframes orbit-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes shoot {
          0% { transform: translateX(0) translateY(0) rotate(15deg); opacity: 0; }
          5% { opacity: 1; }
          50% { opacity: 1; }
          100% { transform: translateX(2000px) translateY(400px) rotate(15deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default IntroAnimation;
